import * as React from 'react'
import { scrollWithin } from '../../lib/scroll-within'

const ESTIMATED_ROW_HEIGHT = 92
const OVERSCAN_PX = 360

function MeasuredRow({
  top,
  rowKey,
  onMeasure,
  children,
}: {
  top: number
  rowKey: string
  onMeasure: (key: string, height: number) => void
  children: React.ReactNode
}) {
  const ref = React.useRef<HTMLDivElement>(null)
  React.useLayoutEffect(() => {
    const element = ref.current
    if (!element) return
    const measure = () => onMeasure(rowKey, Math.ceil(element.getBoundingClientRect().height))
    measure()
    if (!globalThis.ResizeObserver) return
    const observer = new ResizeObserver(measure)
    observer.observe(element)
    return () => observer.disconnect()
  }, [onMeasure, rowKey])

  return (
    <div ref={ref} data-virtual-row={rowKey} className="absolute inset-x-0" style={{ transform: `translateY(${top}px)` }}>
      {children}
    </div>
  )
}

/** Variable-height windowing without owning another package dependency. Only visible rows plus overscan are mounted. */
export function VirtualQueueList<T>({
  items,
  getKey,
  selectedIndex,
  scrollRequest = 0,
  label,
  onNearEnd,
  renderItem,
}: {
  items: T[]
  getKey: (item: T) => string
  selectedIndex: number
  /** Bumped by explicit keyboard navigation; each new value scrolls the selected row into view once. */
  scrollRequest?: number
  label: string
  onNearEnd?: () => void
  renderItem: (item: T, index: number) => React.ReactNode
}) {
  const viewportRef = React.useRef<HTMLDivElement>(null)
  const [scrollTop, setScrollTop] = React.useState(0)
  const [viewportHeight, setViewportHeight] = React.useState(640)
  const [heights, setHeights] = React.useState<ReadonlyMap<string, number>>(() => new Map())
  const pendingScrollKey = React.useRef<string | null>(null)

  React.useLayoutEffect(() => {
    const viewport = viewportRef.current
    if (!viewport) return
    const measure = () => setViewportHeight(viewport.clientHeight || 640)
    measure()
    if (!globalThis.ResizeObserver) return
    const observer = new ResizeObserver(measure)
    observer.observe(viewport)
    return () => observer.disconnect()
  }, [])

  const measure = React.useCallback((key: string, height: number) => {
    if (height <= 0) return
    setHeights((current) => {
      if (current.get(key) === height) return current
      const next = new Map(current)
      next.set(key, height)
      return next
    })
  }, [])

  const layout = React.useMemo(() => {
    const offsets: number[] = []
    let total = 0
    for (const item of items) {
      offsets.push(total)
      total += heights.get(getKey(item)) ?? ESTIMATED_ROW_HEIGHT
    }
    return { offsets, total }
  }, [getKey, heights, items])

  let start = 0
  const lower = Math.max(0, scrollTop - OVERSCAN_PX)
  while (start < items.length - 1 && (layout.offsets[start + 1] ?? 0) < lower) start += 1
  let end = start
  const upper = scrollTop + viewportHeight + OVERSCAN_PX
  while (end < items.length - 1 && (layout.offsets[end] ?? 0) < upper) end += 1

  // Scroll the selection into view ONLY when the caller asks (explicit keyboard navigation bumps
  // `scrollRequest`). Running this on every render/refetch/measure is what yanked the list back to the selected
  // row (row 0 by default) whenever a live refresh landed while the person was scrolling (#89 item 5).
  const latest = React.useRef({ getKey, heights, items, offsets: layout.offsets, selectedIndex })
  latest.current = { getKey, heights, items, offsets: layout.offsets, selectedIndex }
  React.useLayoutEffect(() => {
    if (!scrollRequest) return
    const viewport = viewportRef.current
    const { getKey: keyOf, heights: sizes, items: rows, offsets, selectedIndex: index } = latest.current
    const top = offsets[index]
    if (!viewport || top === undefined) return
    const key = rows[index] ? keyOf(rows[index]) : ''
    const bottom = top + (sizes.get(key) ?? ESTIMATED_ROW_HEIGHT)
    const mounted = [...viewport.querySelectorAll<HTMLElement>('[data-virtual-row]')].find((element) => element.dataset.virtualRow === key)
    if (mounted) {
      scrollWithin(mounted, { block: 'nearest' })
      return
    }

    // Home/End can target a row outside the mounted window. Pre-position the virtual viewport so React mounts it,
    // then the layout effect below finishes with scrollWithin (which never moves the page).
    const nextTop = top < viewport.scrollTop ? top : Math.max(0, bottom - viewport.clientHeight)
    pendingScrollKey.current = key
    viewport.scrollTop = nextTop
    setScrollTop(nextTop)
  }, [scrollRequest])

  React.useLayoutEffect(() => {
    const key = pendingScrollKey.current
    const viewport = viewportRef.current
    if (!key || !viewport) return
    const mounted = [...viewport.querySelectorAll<HTMLElement>('[data-virtual-row]')].find((element) => element.dataset.virtualRow === key)
    if (!mounted) return
    pendingScrollKey.current = null
    scrollWithin(mounted, { block: 'nearest' })
  })

  React.useEffect(() => {
    if (end >= items.length - 5) onNearEnd?.()
  }, [end, items.length, onNearEnd])

  const visible = items.slice(start, end + 1)
  return (
    <div
      ref={viewportRef}
      role="listbox"
      aria-label={label}
      data-virtualised="true"
      className="min-h-0 flex-1 touch-pan-y overflow-y-auto overscroll-contain"
      onScroll={(event) => setScrollTop(event.currentTarget.scrollTop)}
    >
      <div className="relative w-full" style={{ height: layout.total }}>
        {visible.map((item, visibleIndex) => {
          const index = start + visibleIndex
          const key = getKey(item)
          return (
            <MeasuredRow key={key} rowKey={key} top={layout.offsets[index] ?? 0} onMeasure={measure}>
              {renderItem(item, index)}
            </MeasuredRow>
          )
        })}
      </div>
    </div>
  )
}
